// Import UI components from Refine's Ant Design wrapper for building the chat interface
import { AntdList, Avatar, Button, Empty, Icons, Input, Show } from "@pankod/refine-antd";
// Import core hooks and types from Refine framework for resource management and authentication
import { IResourceComponentsProps, useCustomMutation, useGetIdentity } from "@pankod/refine-core";
// Import TypeScript interfaces for type safety (IUser, chatModel)
import { IUser, chatModel } from "interfaces";
// Import React's useState hook for managing component state
import { useState } from "react";
// Import API configuration constants (base URL, resource paths, image paths)
import { API_PATH, OPENAI_LOGO_PATH, RESOURCE_PATH } from "scripts/site";

// Destructure the Search component from Input for the chat message input field
const { Search } = Input;

// Define and export the ChatList component as a React functional component with resource props
export const ChatList: React.FC<IResourceComponentsProps> = () => {
  // State variable to store array of chat messages (user and assistant messages)
  const [chatData, setChatData] = useState<chatModel[]>([]);

  // State variable to track if an API call is currently in progress (shows loading state)
  const [isLoading, setIsLoading] = useState(false);

  // Hook that returns mutate function for making custom API calls/mutations
  const { mutate } = useCustomMutation();

  // State variable to store the current text input from the user in the search box
  const [inputValue, setInputValue] = useState('');

  // Function to handle sending user messages and receiving AI responses from the API
  const apiCall = (userText: string) => {
    // Check if the input text is empty; if so, exit early to prevent sending empty messages
    if (!userText) {
      return;
    }

    // Set loading state to true to show loading indicator during API call
    setIsLoading(true);

    // Clear the chat history if there were any errors in previous messages
    if (chatData.some(c => c.error)) {
      // Remove all items from the beginning of the array (clear errors)
      chatData.splice(0);
      // Update state with the cleared chat data
      setChatData(chatData);
    }

    // Add new user message object to the chat data array
    chatData.push({
      // Identify this message as coming from the user
      actor: "user",
      // Store conversation content in both contextual and standard formats
      conversation: {
        // Contextual version used for AI context (may include system info)
        contextual: userText,
        // Standard version used for display to user
        standard: userText
      },
      // Mark this message as not having an error
      error: false
    });

    // Update state with the new user message added to the chat history
    setChatData(chatData);

    // Clear the input field after sending the message
    setInputValue('');

    // Make an API call to the backend Chat endpoint
    mutate({
      // Construct full API endpoint URL: base path + chat resource path
      url: API_PATH + "/" + RESOURCE_PATH.CHAT,
      // Specify HTTP method as POST to send data to the server
      method: "post",
      // Send the chat context history and current user query
      values: {
        // Include entire conversation history for context
        chatContext: chatData,
        // Include the current user query/question
        query: userText
      }
    },
      {
        // Handler function executed when the API call succeeds and returns data
        onSuccess: (data, variables, context) => {
          // Add assistant's response message to the chat data array
          chatData.push({
            // Identify this message as coming from the AI assistant
            actor: "assistant",
            // Store the API response in both formats
            conversation: {
              // Contextual version of the response from the API
              contextual: data?.data?.contextual,
              // Standard version of the response for display to user
              standard: data?.data?.standard,
            },
            // Mark this message as not having an error
            error: false
          });

          // Update state with the assistant's response added to chat history
          setChatData(chatData);

          // Set loading state to false since the API call is complete
          setIsLoading(false);
        },

        // Handler function executed when the API call fails with an error
        onError: (data, variables, context) => {
          // Add error message to the chat data displayed as assistant response
          chatData.push({
            // Show error message as if from the assistant
            actor: "assistant",
            // Format error details into a readable message
            conversation: {
              // Contextual error message: HTTP status code - status text - error message
              contextual: data?.statusCode + " - " + data?.response?.statusText + ": " + data?.response?.data?.message,
              // Standard error message (same as contextual for errors)
              standard: data?.statusCode + " - " + data?.response?.statusText + ": " + data?.response?.data?.message,
            },
            // Mark this message as having an error
            error: true
          });

          // Update state with the error message added to chat history
          setChatData(chatData);

          // Set loading state to false since the API call is complete
          setIsLoading(false);
        }
      }
    );
  }

  // Hook to retrieve the current authenticated user's identity/profile data
  const { data } = useGetIdentity();

  // Cast the identity data to the IUser interface type for type safety
  const user = data as IUser;

  // Extract the user's alias (name before @ symbol) or default to "User" if not available
  const userAlias = user?.name?.split("@")[0] ?? "User";

  // Return the JSX component UI structure
  return (
    // Show component provides a container with header and standard Refine layout
    <Show
      // Disable the delete button in the header
      canDelete={false}
      // Configure header properties
      headerProps={{
        // Hide the back navigation button
        backIcon: false,
        // Set the page title
        title: "Chat GPT",
        // Show a Clear button in header only if there are chat messages
        extra: chatData.length > 0 && <Button danger icon={<Icons.ClearOutlined />} onClick={() => { setChatData([]) }}>Clear</Button>
      }}>

      {/* Scrollable container div for displaying the chat message list */}
      <div
        id="scrollableDiv"
        style={{
          // Set maximum height for the chat message area
          maxHeight: 500,
          // Enable vertical scrolling when content exceeds max height
          overflow: 'auto',
          // Add spacing between chat list and input field below
          marginBottom: 20,
        }}
      >
        {/* Conditionally render: show chat list if messages exist, otherwise show empty state */}
        {chatData.length > 0 ?
          // List component to display all chat messages in the conversation
          <AntdList
            // Show loading spinner while waiting for API response
            loading={isLoading}
            // Arrange list items vertically (one per row)
            itemLayout="vertical"
            // Disable pagination (show all messages at once with scrolling)
            pagination={false}
            // Use chat data array as the source for list items
            dataSource={chatData}
            // Render function for each message item in the list
            renderItem={(item) => (
              // Check if the message is from the AI assistant
              item.actor === "assistant"
                // Render assistant message with OpenAI logo avatar
                ? <AntdList.Item style={{ border: "none" }}>
                  <AntdList.Item.Meta
                    // Display OpenAI logo as the avatar for assistant messages
                    avatar={<Avatar src={OPENAI_LOGO_PATH} />}
                    // Display "GPT-4" as the assistant name/title
                    title={"GPT-4"}
                    // Display the assistant's response text
                    description={item.conversation.standard} />
                </AntdList.Item>
                // Otherwise, render message as from the user
                : <AntdList.Item style={{ border: "none" }}>
                  <AntdList.Item.Meta
                    // Display user avatar icon
                    avatar={<Avatar src={"user.png"} />}
                    // Display the user's name/alias as the title
                    title={userAlias}
                    // Display the user's message text
                    description={item.conversation.standard} />
                </AntdList.Item>
            )}
          >
          </AntdList>
          // Show empty state message when no chat messages exist
          : <Empty description="No conversations" image={Empty.PRESENTED_IMAGE_DEFAULT}></Empty>}
      </div>

      {/* Search input component for user to type and send chat messages */}
      <Search
        // Placeholder text shown when input is empty
        placeholder="Ask me anything"
        // Custom button text with send icon displayed on the right side
        enterButton={<>Send  <Icons.SendOutlined /> </>}
        // Set input field and button size to large for better visibility
        size="large"
        // Show loading spinner on button while API call is in progress
        loading={isLoading}
        // Bind input field value to state variable
        value={inputValue}
        // Mark this field as required (for form validation)
        required
        // Update state as user types in the input field
        onChange={(e) => setInputValue(e.target.value)}
        // Call apiCall function when user presses Enter or clicks Send button
        onSearch={apiCall}
      />
    </Show>
  );
};